import { parseNovelDramaDelivered, parseNovelDramaEnded, parseNovelDramaSession, parseNovelDramaSpeech, parseNovelDramaVideoLookup, parseNovelDramaVideoSaved } from '@drama/contracts/novel'

/**
 * 互动小说数字人演出（/api/novel/runs/:id/drama/sessions，C29–C31）。控制连接走会话返回的 ws_path（本服务中继、一次性 token），不经 transport。
 * 建会话失败：402 INSUFFICIENT_BALANCE（details 带 required_points、current_balance、shortfall_points）、409 CONFLICT drama_busy / request_id_conflict、
 * 400 BAD_REQUEST drama_unavailable / drama_scene_unavailable、502 drama_start_failed（已退款；错误码在 code 上）。
 * @param {{ request: Function }} transport
 */
export function createNovelDramaApi(transport, { prefix = '/api/novel/runs' } = {}) {
  const sessionsPath = (run_id) => `${prefix}/${encodeURIComponent(run_id)}/drama/sessions`
  return {
    lookupVideo({ run_id, request_id, provider }) {
      return transport.request('POST', `${prefix}/${encodeURIComponent(run_id)}/drama/video-cache`, {
        body: { request_id, provider }, parse: parseNovelDramaVideoLookup, timeoutMs: 15000,
      })
    },
    video({ run_id, asset_key }) {
      return transport.binary('GET', `${prefix}/${encodeURIComponent(run_id)}/drama/videos/${encodeURIComponent(asset_key)}`, { timeoutMs: 60000 })
    },
    saveVideo({ run_id, session_id, blob }) {
      return transport.request('POST', `${sessionsPath(run_id)}/${encodeURIComponent(session_id)}/video`, {
        body: blob, parse: parseNovelDramaVideoSaved, timeoutMs: 120000,
      })
    },
    /**
     * 建一场：按 novel_drama_session 预扣积分。request_id 是回合的 request_id，attempt 是该回合第几次建会话（从 1 起，重试 +1）。
     * @param {{ run_id: string, request_id: string, attempt: number, character_id: string, state_id: string }} args
     */
    startSession({ run_id, request_id, attempt, character_id, state_id, location_id, provider = 'auto' }) {
      return transport.request('POST', sessionsPath(run_id), { body: { request_id, attempt, character_id, state_id, location_id, provider }, parse: parseNovelDramaSession })
    },
    /** 同一场在提交首段演出之前改选供应商，沿用逻辑场次和扣费。 */
    retrySession({ run_id, session_id, failed_provider, live_id, reason = 'connect_failed' }) {
      return transport.request('POST', `${sessionsPath(run_id)}/${encodeURIComponent(session_id)}/retry`, {
        body: { failed_provider, live_id, reason }, parse: parseNovelDramaSession,
      })
    },
    /** 报告当前供应商尝试的实际媒体首帧；不能替代服务端就绪和计费校验。 */
    reportDelivered({ run_id, session_id, provider, live_id }) {
      return transport.request('POST', `${sessionsPath(run_id)}/${encodeURIComponent(session_id)}/delivered`, {
        body: { provider, live_id }, parse: parseNovelDramaDelivered, timeoutMs: 5000,
      })
    },
    /** 会话人物一段对白的配音（不另扣积分）。 @param {{ run_id: string, session_id: string, segment_id: string }} args */
    speech({ run_id, session_id, segment_id }) {
      return transport.request('POST', `${sessionsPath(run_id)}/${encodeURIComponent(session_id)}/speech`, { body: { segment_id }, parse: parseNovelDramaSpeech })
    },
    /**
     * 结束一场；首帧前失败的原因触发退款。
     * @param {{ run_id: string, session_id: string, reason: string, metrics?: Record<string, number|null> }} args
     */
    endSession({ run_id, session_id, reason, metrics }) {
      const body = metrics === undefined ? { reason } : { reason, metrics }
      return transport.request('DELETE', `${sessionsPath(run_id)}/${encodeURIComponent(session_id)}`, { body, parse: parseNovelDramaEnded })
    },
  }
}
